import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuthUser } from './AuthUserContext';
import { useAuth0 } from '../components/auth/DevAuth0Provider';
import { fetchOrganizations } from '../api/auth0Api';
import { useDataAccess } from '../hooks/useDataAccess';
import { pageCategories } from '../utils/navigationConfig';
import { navigateWithRefresh } from '../utils/impersonationUtils';

const CustomerContext = createContext({
  currentCustomer: null,
  isImpersonating: false,
  originalAdmin: null,
  availableCustomers: [],
  switchCustomer: () => {},
  exitImpersonation: () => {},
  getEffectiveCustomerId: () => null,
  canAccessCustomer: () => false,
  isLoading: true,
  error: null
});

export const CustomerProvider = ({ children }) => {
  const user = useAuthUser();
  // const { makeCustomerApiRequest } = useDataAccess();
  const { getAccessTokenSilently } = useAuth0();
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdmin, setOriginalAdmin] = useState(null);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Rate limiting for organizations API
  const [lastOrganizationsFetch, setLastOrganizationsFetch] = useState(0);
  const ORGANIZATIONS_CACHE_DURATION = 300000; // 5 minutes

  // Extract customer information from user
  const extractCustomerFromUser = useCallback(async (userProfile) => {
    if (!userProfile) return null;

    // For Helios admin users, always use celeste
    if (userProfile.isHeliosAdmin) {
      // console.log('CustomerContext: Helios admin user detected - using celeste client');
      return {
        id: 'celeste',
        name: 'Helios (Celeste)',
        clientId: 'celeste',
        category: 'voiceAI',
        chatEndpoint: '/celeste/chat',
        organizationId: userProfile.organizationId
      };
    }

    // Get customer info from Auth0 custom claims (including client_admin users)
    const customerId = userProfile.customerId ||
                      userProfile.tenantId ||
                      'unknown';

    const clientId = userProfile.clientId || customerId;
    const chatEndpoint = userProfile.chatEndpoint || `/${clientId}/chat`;

    // console.log('CustomerContext: Extracting customer for user:', {
    //   customerId,
    //   clientId,
    //   roles: userProfile.roles,
    //   organizationId: userProfile.organizationId
    // });

    // For client users, try to fetch full organization data to get logo and other details
    if (customerId !== 'unknown' && userProfile.organizationId) {
      try {
        // console.log('CustomerContext: Fetching full organization data for client user...');
        const organizations = await fetchOrganizations(getAccessTokenSilently);
        const userOrganization = organizations.find(org => org.id === userProfile.organizationId);
        
        if (userOrganization) {
          // console.log('CustomerContext: Found user organization:', userOrganization);
          
          // Map organization data to customer format (similar to admin impersonation)
          const customer = {
            id: customerId,
            name: userOrganization.display_name || userOrganization.name || getCustomerDisplayName(customerId),
            clientId: clientId,
            chatEndpoint: chatEndpoint,
            organizationId: userProfile.organizationId,
            displayName: userOrganization.display_name || userOrganization.name,
            logoUrl: userOrganization.branding?.logo_url, // Include logo URL
            category: userOrganization.metadata?.category || 'general',
            metadata: userOrganization.metadata
          };
          
          // console.log('CustomerContext: Mapped client organization to customer:', customer);
          return customer;
        }
      } catch (error) {
        console.error('CustomerContext: Error fetching organization data for client:', error);
        // Fall back to basic customer data if API call fails
      }
    }

    // Fallback to basic customer data
    return {
      id: customerId,
      name: getCustomerDisplayName(customerId),
      clientId: clientId,
      chatEndpoint: chatEndpoint,
      organizationId: userProfile.organizationId
    };
  }, [getAccessTokenSilently]);

  // Get display name for customer ID
  const getCustomerDisplayName = (customerId) => {
    const customerNames = {
      'helios': 'Helios',
      'wards': "Ward's Rental",
      'celeste': 'Helios (Celeste)',
      'unknown': 'Unknown Customer'
    };
    return customerNames[customerId] || customerId;
  };

  // Load available customers for admin users
  const loadAvailableCustomers = useCallback(async () => {
    if (!user?.isHeliosAdmin) {
      setAvailableCustomers([]);
      return;
    }

    // Rate limiting check
    const now = Date.now();
    if (now - lastOrganizationsFetch < ORGANIZATIONS_CACHE_DURATION) {
      // console.log('Skipping organizations fetch due to rate limiting');
      return;
    }

    try {
      setError(null);
      // console.log('Loading available customers for impersonation...');
      const organizations = await fetchOrganizations(getAccessTokenSilently);
      // console.log('Fetched organizations:', organizations);
      setLastOrganizationsFetch(now);

      const customers = organizations.map(org => {
        // Map organization names to our expected customer IDs
        const orgNameToCustomerId = {
          'wards': 'wards',
          'helios': 'helios',
          'scale': 'scale',
          // Add more mappings as needed
        };

        // Try to get customer info from metadata first, then map org name, then fall back to org name
        const customerId = org.metadata?.customer_id ||
                          orgNameToCustomerId[org.name.toLowerCase()] ||
                          org.name.toLowerCase();
        const clientId = org.metadata?.client_id || org.metadata?.customer_id || customerId;

        const customer = {
          id: customerId,
          name: org.display_name || org.name,
          clientId: clientId,
          category: org.metadata?.category || 'general',
          chatEndpoint: org.metadata?.chat_endpoint || `/${clientId}/chat`,
          organizationId: org.id,
          displayName: org.display_name || org.name,
          logoUrl: org.branding?.logo_url, // Add logo URL from blob storage
          metadata: org.metadata
        };

        // console.log("CustomerContext: Mapped organization to customer:", {
        //   orgName: org.name,
        //   orgDisplayName: org.display_name,
        //   orgMetadata: org.metadata,
        //   orgBranding: org.branding,
        //   logoUrl: org.branding?.logo_url,
        //   mappedCustomer: customer
        // });

        return customer;
      });

      // console.log('Mapped customers for impersonation:', customers);
      setAvailableCustomers(customers);
    } catch (err) {
      console.error('Error loading available customers:', err);
      setError(`Failed to load customers: ${err.message}`);
      setAvailableCustomers([]);
    }
  }, [user?.isHeliosAdmin, getAccessTokenSilently, lastOrganizationsFetch]);

  // Switch to a different customer (admin impersonation)
  const switchCustomer = useCallback(async (customerId) => {
    // console.log("CustomerContext: switchCustomer called with:", customerId);
    // console.log("CustomerContext: Available customers:", availableCustomers);

    if (!user?.isHeliosAdmin) {
      console.warn('Only Helios admins can switch customers');
      return;
    }

    const targetCustomer = availableCustomers.find(c => c.id === customerId);
    // console.log("CustomerContext: Found target customer:", targetCustomer);

    if (!targetCustomer) {
      console.error('Customer not found:', customerId, 'in available customers:', availableCustomers);
      return;
    }

    try {
      // Store original admin info if not already impersonating
      if (!isImpersonating) {
        const adminCustomer = await extractCustomerFromUser(user);
        setOriginalAdmin({
          ...user,
          customer: adminCustomer
        });
      }

      // Update state
      setCurrentCustomer(targetCustomer);
      setIsImpersonating(true);

      // Fix circular reference by getting customer data first
      const adminCustomer = await extractCustomerFromUser(user);
      
      // Store impersonation state in sessionStorage for persistence
      sessionStorage.setItem('impersonation_state', JSON.stringify({
        isImpersonating: true,
        currentCustomer: targetCustomer,
        originalAdmin: originalAdmin || {
          ...user,
          customer: adminCustomer
        }
      }));

      // console.log(`Admin ${user.email} is now impersonating customer: ${targetCustomer.name}`);

      // Navigate to customer's home page with refresh that preserves authentication
      // (same path shape as RoleBasedRedirect: /app/:customerId/:customerId-home)
      const customerPath = `/app/${targetCustomer.id}/${targetCustomer.id}-home`;
      navigateWithRefresh(customerPath);
    } catch (err) {
      console.error('Error during customer impersonation:', err);
    }
  }, [user, availableCustomers, isImpersonating, originalAdmin, extractCustomerFromUser]);

  // Exit impersonation and return to admin view
  const exitImpersonation = useCallback(async (onComplete) => {
    if (!isImpersonating || !originalAdmin) {
      console.warn('Not currently impersonating');
      return;
    }

    try {
      // console.log('🔄 Starting exit impersonation process...');

      // Return to the original admin's customer context
      const adminCustomer = await extractCustomerFromUser(originalAdmin);
      // console.log('🔄 Admin customer extracted:', adminCustomer);
      
      setCurrentCustomer(adminCustomer);
      setIsImpersonating(false);
      setOriginalAdmin(null);

      // Complete cleanup of all impersonation-related state from sessionStorage
      sessionStorage.removeItem('impersonation_state');
      sessionStorage.removeItem('impersonation_loading_shown');
      sessionStorage.removeItem('impersonation_cleanup_ran');

      // console.log(`🔄 Exited impersonation, returned to admin view`);

      // Execute callback if provided (for backward compatibility)
      if (typeof onComplete === 'function') {
        onComplete();
      }

      // Navigate to admin dashboard with refresh that preserves authentication
      const adminPath = '/app/admin-dashboard';
      navigateWithRefresh(adminPath);
    } catch (err) {
      console.error('Error during exit impersonation:', err);
    }
  }, [isImpersonating, originalAdmin, extractCustomerFromUser]);

  // Get the effective customer ID for API calls
  const getEffectiveCustomerId = useCallback(() => {
    // SECURITY: If impersonating, NEVER fall back to celeste or any other customer
    // If impersonating but currentCustomer is missing, return null to indicate error
    if (isImpersonating) {
      if (currentCustomer?.id) {
        return currentCustomer.id;
      }
      // During impersonation, if customer data is missing, return null to prevent data leakage
      // This ensures we don't accidentally show celeste data when impersonating another customer
      console.warn('CustomerContext: Impersonation active but currentCustomer is missing - returning null to prevent data leakage');
      return null;
    }

    // For Helios admin users (when NOT impersonating), always use celeste
    if (user?.isHeliosAdmin) {
      // console.log('CustomerContext: Helios admin user - using celeste');
      return 'celeste';
    }

    // For all other users (including client_admin), return their customer ID
    const effectiveId = currentCustomer?.id || 'unknown';
    // console.log('CustomerContext: Using effective customer ID:', effectiveId, 'for user:', user?.email);
    return effectiveId;
  }, [currentCustomer, user?.isHeliosAdmin, user?.email, isImpersonating]);

  // Check if user can access a specific customer's data
  const canAccessCustomer = useCallback((customerId) => {
    // Helios admins can access any customer
    if (user?.isHeliosAdmin) {
      return true;
    }

    // Regular users can only access their own customer
    const userCustomerId = user?.customerId || user?.tenantId;

    // console.log(
    //   '%CAN ACCESS CHECK - Target:', 'color: red; font-weight: bold;',
    //    customerId,
    //    'User Customer ID:', userCustomerId
    // );
    return userCustomerId === customerId;
  }, [user]);

  // Get effective chat endpoint
  const getEffectiveChatEndpoint = useCallback(() => {
    return currentCustomer?.chatEndpoint || '/unknown/chat';
  }, [currentCustomer]);

  // Auto-redirect when impersonation ends (for session timeouts, etc.)
  const [redirectCallback, setRedirectCallback] = useState(null);

  // Monitor impersonation state changes and trigger redirect if needed
  useEffect(() => {
    // If we were impersonating but now we're not, and we have a redirect callback, execute it
    if (!isImpersonating && redirectCallback && typeof redirectCallback === 'function') {
      // console.log('Impersonation ended, executing auto-redirect callback');
      redirectCallback();
      setRedirectCallback(null); // Clear the callback after use
    }
  }, [isImpersonating, redirectCallback]);

  // Function to set up auto-redirect for when impersonation ends
  const setAutoRedirect = useCallback((callback) => {
    setRedirectCallback(() => callback);
  }, []);

  const contextValue = {
    currentCustomer,
    isImpersonating,
    originalAdmin,
    availableCustomers,
    switchCustomer,
    exitImpersonation,
    getEffectiveCustomerId,
    canAccessCustomer,
    getEffectiveChatEndpoint,
    setAutoRedirect,
    isLoading,
    error
  };

  // This local API function breaks the circular dependency with useDataAccess
  const makeApiRequestForContext = useCallback(async (endpoint, options = {}, customerId) => {
    try {
      // Validate customer ID
      if (!customerId || customerId === 'unknown') {
        throw new Error("Cannot make API request without a valid customer ID.");
      }

      // Validate user permissions
      const userIsAdmin = user?.isHeliosAdmin;
      const userCustomerId = user?.customerId || user?.tenantId;
      if (!userIsAdmin && userCustomerId !== customerId) {
          throw new Error(`Access denied: Cannot access customer '${customerId}' data.`);
      }

      // Prepare request URL
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://localhost:5000';
      const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
      const url = `${baseUrl}/api/customers/${customerId}/${cleanEndpoint}`;

      // Get authentication token
      let token;
      try {
        token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email'
        });
      } catch (authError) {
        console.error('Authentication error:', authError);
        throw new Error(`Authentication failed: ${authError.message || 'Could not get access token'}`);
      }

      // Prepare request options
      const requestOptions = {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...options.headers
          },
          ...options
      };

      // Make the request
      let response;
      try {
        response = await fetch(url, requestOptions);
      } catch (networkError) {
        console.error('Network error:', networkError);
        throw new Error(`Network error: ${networkError.message || 'Failed to connect to server'}`);
      }

      // Handle response
      if (!response.ok) {
        let errorMessage = `API request failed: ${response.status}`;
        
        try {
          const errorData = await response.json();
          if (errorData && errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If JSON parsing fails, use the status text
          errorMessage = `${response.status}: ${response.statusText || errorMessage}`;
        }
        
        throw new Error(errorMessage);
      }
      
      return await response.json();
    } catch (error) {
      console.error('CustomerContext local API request failed:', error);
      throw error;
    }
  }, [user, getAccessTokenSilently]); // Depends on user and getToken, which are stable


  // Initialize customer context when user changes
  useEffect(() => {
    const initializeCustomerContext = async () => {
      // console.log('%cCUSTOMER CONTEXT INIT - User object:', 'color: blue; font-weight: bold;', user);
      setIsLoading(true);
      setError(null);

      // Wait until AuthUser profile is populated; a truthy but empty `{}` user skips
      // impersonation restore (isHeliosAdmin undefined) and can leave /app/:customerId unauthorized.
      if (!user?.email) {
        setCurrentCustomer(null);
        setAvailableCustomers([]);
        setIsLoading(true);
        return;
      }

      try {
        // PRIORITY 1: Check for and restore a valid impersonation state.
        const savedStateJSON = sessionStorage.getItem('impersonation_state');
        if (savedStateJSON && user.isHeliosAdmin) {
          try {
            const savedState = JSON.parse(savedStateJSON);
            if (savedState.isImpersonating && savedState.currentCustomer?.id && savedState.currentCustomer.id !== 'unknown') {
              // console.log('Restoring impersonation state from sessionStorage:', savedState.currentCustomer);
              setCurrentCustomer(savedState.currentCustomer);
              setIsImpersonating(true);
              setOriginalAdmin(savedState.originalAdmin);
              await loadAvailableCustomers();
              setIsLoading(false);
              return; // EXIT EARLY - We have our impersonated context.
            }
          } catch (parseError) {
            console.error('Error parsing impersonation state:', parseError);
            // Clear invalid state
            sessionStorage.removeItem('impersonation_state');
            setError('Failed to restore impersonation state');
          }
        }
        
        // PRIORITY 2: If not impersonating, run one-time cleanup and then proceed with default context.
        const cleanupRan = sessionStorage.getItem('impersonation_cleanup_ran');
        if (!cleanupRan) {
            try {
              const stateJSON = sessionStorage.getItem('impersonation_state');
              if (stateJSON) {
                  const state = JSON.parse(stateJSON);
                  if (state.currentCustomer && (state.currentCustomer.id === 'unknown' || state.currentCustomer.clientId === 'unknown')) {
                      // console.log('Clearing bad impersonation state on initialization');
                      sessionStorage.removeItem('impersonation_state');
                  }
              }
            } catch (cleanupError) {
              console.error('Error during impersonation cleanup:', cleanupError);
              // Clear potentially corrupted state
              sessionStorage.removeItem('impersonation_state');
            }
            sessionStorage.setItem('impersonation_cleanup_ran', 'true');
        }
        
        // console.log("No valid impersonation state found, initializing default context.");
        let userCustomer;
        try {
          userCustomer = await extractCustomerFromUser(user);
        } catch (extractError) {
          console.error('Error extracting customer from user:', extractError);
          setError(`Failed to extract customer info: ${extractError.message}`);
          setIsLoading(false);
          return;
        }
        
        if (userCustomer && userCustomer.id !== 'unknown') {
            // If we already have full customer details from extractCustomerFromUser, use them
            if (userCustomer.logoUrl || userCustomer.metadata) {
                setCurrentCustomer(userCustomer);
            } else {
                try {
                  // Fallback to API call for additional details
                  const fullCustomerDetails = await makeApiRequestForContext(`info`, {}, userCustomer.id);
                  setCurrentCustomer(fullCustomerDetails);
                } catch (apiError) {
                  console.error('Error fetching additional customer details:', apiError);
                  // Fall back to basic customer info
                  setCurrentCustomer(userCustomer);
                }
            }
        } else {
            setCurrentCustomer(userCustomer);
        }

        if (user.isHeliosAdmin) {
          try {
            await loadAvailableCustomers();
          } catch (loadError) {
            console.error('Error loading available customers:', loadError);
            // Don't fail the whole initialization for this
          }
        }

      } catch (err) {
        console.error('Error initializing customer context:', err);
        setError(`Failed to initialize customer context: ${err.message || 'Unknown error'}`);
      } finally {
        setIsLoading(false);
      }
    };

    // Start initialization
    initializeCustomerContext();
    
    // Cleanup function to handle component unmounting
    return () => {
      // Clear any pending operations or state if needed
      // console.log('CustomerContext cleanup on unmount');
    };
  }, [user, extractCustomerFromUser, loadAvailableCustomers, isImpersonating, makeApiRequestForContext]);

  return (
    <CustomerContext.Provider value={contextValue}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
};

export default CustomerContext;